import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Search, ShieldCheck, TimerReset, AlertTriangle } from "lucide-react";
import { ShadowModeBanner } from "@/components/admin/ShadowModeBanner";

interface RunRow {
  id: string;
  created_at: string;
  query: string;
  provider: string;
  model_used: string | null;
  cache_hit: boolean;
  success: boolean;
  cove_applied: boolean;
  hedge_applied: boolean;
  latency_ms: number | null;
  result_count: number;
  error: string | null;
}

interface CacheRow {
  id: string;
  normalized_query: string;
  provider: string;
  model_used: string;
  result_count: number;
  expires_at: string;
  updated_at: string;
}

export default function FactualHealth() {
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [cacheRows, setCacheRows] = useState<CacheRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [{ data: factualRuns }, { data: factualCache }] = await Promise.all([
        supabase
          .from("syra_factual_search_runs" as never)
          .select("id, created_at, query, provider, model_used, cache_hit, success, cove_applied, hedge_applied, latency_ms, result_count, error")
          .order("created_at", { ascending: false })
          .limit(50),
        supabase
          .from("syra_factual_search_cache" as never)
          .select("id, normalized_query, provider, model_used, result_count, expires_at, updated_at")
          .order("updated_at", { ascending: false })
          .limit(25),
      ]);
      if (!cancelled) {
        setRuns((factualRuns as RunRow[]) ?? []);
        setCacheRows((factualCache as CacheRow[]) ?? []);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = useMemo(() => {
    const total = runs.length;
    const success = runs.filter((row) => row.success).length;
    const cacheHits = runs.filter((row) => row.cache_hit).length;
    const cove = runs.filter((row) => row.cove_applied).length;
    const avgLatency = total
      ? Math.round(runs.reduce((sum, row) => sum + (row.latency_ms ?? 0), 0) / total)
      : 0;
    return {
      total,
      successRate: total ? Math.round((success / total) * 100) : 0,
      cacheRate: total ? Math.round((cacheHits / total) * 100) : 0,
      coveRate: total ? Math.round((cove / total) * 100) : 0,
      avgLatency,
    };
  }, [runs]);

  if (loading) {
    return (
      <div className="container mx-auto flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando health factual…
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-6 p-6">
      <ShadowModeBanner compact />
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">Busca factual</h1>
        <p className="text-sm text-muted-foreground">S28 · Perplexity, cache dedicado, hedge honesto e CoVe leve.</p>
      </header>

      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard title="Execuções" value={String(metrics.total)} icon={Search} />
        <MetricCard title="Sucesso" value={`${metrics.successRate}%`} icon={ShieldCheck} />
        <MetricCard title="Cache hit" value={`${metrics.cacheRate}%`} icon={TimerReset} />
        <MetricCard title="CoVe aplicado" value={`${metrics.coveRate}%`} icon={AlertTriangle} hint={`${metrics.avgLatency} ms média`} />
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Execuções recentes</CardTitle>
          <CardDescription>Últimos 50 calls classificadas como factual search.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quando</TableHead>
                <TableHead>Consulta</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Latência</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{new Date(row.created_at).toLocaleString("pt-BR")}</TableCell>
                  <TableCell className="max-w-[420px] truncate text-sm">{row.query}</TableCell>
                  <TableCell className="text-xs"><div>{row.provider}</div><div className="text-muted-foreground">{row.model_used ?? "—"}</div></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={row.success ? "default" : "destructive"}>{row.success ? "ok" : "erro"}</Badge>
                      {row.cache_hit ? <Badge variant="secondary">cache</Badge> : null}
                      {row.cove_applied ? <Badge variant="outline">CoVe</Badge> : null}
                      {row.hedge_applied ? <Badge variant="outline">hedge</Badge> : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{row.latency_ms ?? 0} ms · {row.result_count} fontes</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cache factual</CardTitle>
          <CardDescription>Entradas quentes para consultas recentes.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          {cacheRows.length === 0 ? <p className="text-sm text-muted-foreground">Sem entradas de cache ainda.</p> : cacheRows.map((row) => (
            <div key={row.id} className="flex flex-col gap-1 rounded-lg border p-4 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <div className="truncate font-medium">{row.normalized_query}</div>
                <div className="text-xs text-muted-foreground">{row.provider} · {row.model_used} · {row.result_count} fontes</div>
              </div>
              <div className="text-xs text-muted-foreground">expira {new Date(row.expires_at).toLocaleString("pt-BR")}</div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ title, value, icon: Icon, hint }: { title: string; value: string; icon: typeof Search; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold">{value}</div>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}
